/**
 * Teacher Service
 *
 * API service for teacher dashboard functionality.
 */

import type {
  ClassGroupDetail,
  CreateScheduleChangeRequest,
  ScheduleChange,
  TeacherCalendarData,
  TeacherDashboardData,
  TeacherStudentsData,
  UpdateClassLinksRequest,
  UpdateLevelLinksRequest,
} from "../types/teacher";
import api from "./api";

// Helper to convert snake_case to camelCase
function toCamelCase(obj: Record<string, unknown>): Record<string, unknown> {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj))
    return obj.map((item) =>
      toCamelCase(item as Record<string, unknown>),
    ) as unknown as Record<string, unknown>;

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase(),
    );
    result[camelKey] =
      typeof value === "object" && value !== null
        ? toCamelCase(value as Record<string, unknown>)
        : value;
  }
  return result;
}

// Helper to convert camelCase to snake_case
function toSnakeCase(obj: Record<string, unknown>): Record<string, unknown> {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj))
    return obj.map((item) =>
      toSnakeCase(item as Record<string, unknown>),
    ) as unknown as Record<string, unknown>;

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(
      /[A-Z]/g,
      (letter) => `_${letter.toLowerCase()}`,
    );
    result[snakeKey] =
      typeof value === "object" && value !== null
        ? toSnakeCase(value as Record<string, unknown>)
        : value;
  }
  return result;
}

/**
 * Get teacher dashboard overview data
 */
export async function getTeacherDashboard(): Promise<TeacherDashboardData> {
  const response = await api.get("/teacher/dashboard/");
  return toCamelCase(response.data) as unknown as TeacherDashboardData;
}

/**
 * Get all class groups for teacher
 */
export async function getTeacherClasses(params?: {
  level?: string;
  teacher?: string;
}): Promise<ClassGroupDetail[]> {
  const response = await api.get("/teacher/classes/", { params });

  // Handle both paginated and non-paginated responses
  let data: unknown[];
  if (Array.isArray(response.data)) {
    data = response.data;
  } else if (response.data?.results && Array.isArray(response.data.results)) {
    data = response.data.results;
  } else {
    data = [];
  }

  return data.map((item) =>
    toCamelCase(item as Record<string, unknown>),
  ) as unknown as ClassGroupDetail[];
}

/**
 * Get a single class group by ID
 */
export async function getClassGroup(id: string): Promise<ClassGroupDetail> {
  const response = await api.get(`/teacher/classes/${id}/`);
  return toCamelCase(response.data) as unknown as ClassGroupDetail;
}

/**
 * Update class group links (meet_link, whiteboard_url)
 */
export async function updateClassLinks(
  id: string,
  data: UpdateClassLinksRequest,
): Promise<ClassGroupDetail> {
  const snakeData = toSnakeCase(data as unknown as Record<string, unknown>);
  console.log("[updateClassLinks] Input data:", data);
  console.log("[updateClassLinks] Snake case data:", snakeData);
  console.log("[updateClassLinks] URL:", `/teacher/classes/${id}/update_links/`);
  const response = await api.patch(
    `/teacher/classes/${id}/update_links/`,
    snakeData,
  );
  console.log("[updateClassLinks] Response:", response.data);
  const result = toCamelCase(response.data) as unknown as ClassGroupDetail;
  console.log("[updateClassLinks] Converted result:", result);
  return result;
}

/**
 * Update level links (whiteboard, homework) for a class group's level
 */
export async function updateLevelLinks(
  classId: string,
  data: UpdateLevelLinksRequest,
): Promise<ClassGroupDetail> {
  const snakeData = toSnakeCase(data as unknown as Record<string, unknown>);
  const response = await api.patch(
    `/teacher/classes/${classId}/update_level_links/`,
    snakeData,
  );
  return toCamelCase(response.data) as unknown as ClassGroupDetail;
}

/**
 * Get all students for teacher
 */
export async function getTeacherStudents(params?: {
  level?: string;
  classGroup?: string;
  search?: string;
}): Promise<TeacherStudentsData> {
  const snakeParams = params
    ? toSnakeCase(params as unknown as Record<string, unknown>)
    : undefined;
  const response = await api.get("/teacher/students/", { params: snakeParams });
  return toCamelCase(response.data) as unknown as TeacherStudentsData;
}

/**
 * Get teacher calendar data
 */
export async function getTeacherCalendar(params?: {
  year?: number;
  month?: number;
}): Promise<TeacherCalendarData> {
  const response = await api.get("/teacher/calendar/", { params });
  return toCamelCase(response.data) as unknown as TeacherCalendarData;
}

/**
 * Create a schedule change (cancel or reschedule a class)
 */
export async function createScheduleChange(
  data: CreateScheduleChangeRequest,
): Promise<ScheduleChange> {
  const snakeData = toSnakeCase(data as unknown as Record<string, unknown>);
  const response = await api.post("/teacher/schedule-changes/", snakeData);
  return toCamelCase(response.data) as unknown as ScheduleChange;
}

/**
 * Get schedule changes for teacher's classes
 */
export async function getScheduleChanges(params?: {
  classGroup?: string;
  isActive?: boolean;
}): Promise<ScheduleChange[]> {
  const snakeParams = params
    ? toSnakeCase(params as unknown as Record<string, unknown>)
    : undefined;
  const response = await api.get("/teacher/schedule-changes/", {
    params: snakeParams,
  });

  // Handle paginated response or direct array
  const data = Array.isArray(response.data) ? response.data : response.data?.results || [];

  return data.map(
    (item: unknown) => toCamelCase(item as Record<string, unknown>) as unknown as ScheduleChange,
  );
}

/**
 * Delete a schedule change
 */
export async function deleteScheduleChange(id: string): Promise<void> {
  await api.delete(`/teacher/schedule-changes/${id}/`);
}

export default {
  getTeacherDashboard,
  getTeacherClasses,
  getClassGroup,
  updateClassLinks,
  updateLevelLinks,
  getTeacherStudents,
  getTeacherCalendar,
  createScheduleChange,
  getScheduleChanges,
  deleteScheduleChange,
};
